import { useAuth } from 'auth';
import { BannerContext } from 'components/Routes/BannerContext';
import ECSWorldProvider from 'components/editor/ECSWorldProvider';
import MarkupKitProvider from 'components/editor/MarkupKitProvider';
import BluebeamLogo from 'components/Icons/BluebeamLogo';
import ProjectRoverHeader from 'components/Icons/ProjectRoverHeader';
import GeolocationModal from 'components/modals/Geolocation/GeolocationModal';
import TopNav from 'components/Navigation/TopNav';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import PlaceRFIBtn from 'components/viewer/PlaceRFIBtn';
import { environmentVars } from 'constants/services';
import React, { useContext, useEffect, useState } from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { ViewMapLayers } from 'records/interactions/ViewMapLayers';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import { GeoLocation } from 'records/GeoLocation';
import ECSMap from './ECSMap/ECSMap';
import ECSMapSheet from './ECSMapSheet';
import ManagePlacePunchInteraction from './ManagePlacePunchInteraction';
import ManagePostRFIInteraction from './ManagePostRFIInteraction';
import ManageUpdatePunchInteraction from './ManageUpdatePunchInteraction';
import ManageViewRFIInteraction from './ManageViewRFIInteraction';
import MapPageSubheader from './MapPageSubheader';
import PlacePunchBtn from './PlacePunchBtn';
import PlacePunchFooter from './PlacePunchFooter';
import PlacePunchMessageOverlay from './PlacePunchMessageOverlay';
import PostRFIFooter from './PostRFIFooter';
import PostRFIMessageOverlay from './PostRFIMessageOverlay';
import { Project, ProjectGeoLocation } from '../../records/Project';
import { getProject } from '../../services/projectService';

interface Props extends WithAppStoreProps, RouteComponentProps {}

const MapPage: React.FC<Props> = ({ actions, appState }: Props) => {
  const [project, setProject] = useState<Project | null>(null);
  const { sheets, roles } = appState;
  const { userId } = useAuth();

  const { height: bannerHeight } = useContext(BannerContext);
  const userRole = roles.get(userId, false);

  const shouldShowGeolocationModal = (geolocation?: ProjectGeoLocation) => {
    const noLayersOnMap = appState.getCurrentMappedSheetEntries().size === 0;
    const hasGeoLocation = geolocation && geolocation?.center?.length > 0;

    return (
      userRole &&
      userRole.role === 'admin' &&
      noLayersOnMap &&
      sheets.size > 0 &&
      !hasGeoLocation
    );
  };

  const [showGeolocationModal, setShowGeolocationModal] =
    useState<boolean>(false);

  const handleCloseGeoLocationModal = () => {
    setShowGeolocationModal(false);
  };
  const getProjectData = async () => {
    const projectData = await getProject(
      appState.selectedProject,
      appState.userLicense.package
    );

    const noLayersOnMap = appState.getCurrentMappedSheetEntries().size === 0;
    const hasAppStateGeolocationCenter =
      appState.geoLocation.center.length === 0;
    const hasProjectGeolocationCenter =
      projectData?.geolocation?.center?.length > 0;
    const checkIsCenterDifferent =
      appState?.geoLocation?.center[0] !==
        projectData?.geolocation?.center[0] &&
      appState?.geoLocation?.center[1] !== projectData?.geolocation?.center[1];

    if (
      (hasAppStateGeolocationCenter && noLayersOnMap) ||
      (hasProjectGeolocationCenter && checkIsCenterDifferent)
    ) {
      const projectGeoLocation = new GeoLocation({
        bbox: projectData.geolocation?.bbox,
        center: projectData.geolocation?.center,
      });
      actions.setProjectGeoLocation(projectGeoLocation);
    } else if (noLayersOnMap) {
      actions.setProjectGeoLocation(new GeoLocation());
    }

    setProject(projectData);
  };

  const { mapOverlayMachineService } = useMapOverlayMachine();
  useEffect(() => {
    getProjectData();
  }, []);

  useEffect(() => {
    if (shouldShowGeolocationModal(project?.geolocation)) {
      setShowGeolocationModal(true);

      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
      actions.setProjectGeoLocation(new GeoLocation());
    }
  }, [appState.getCurrentMappedSheetEntries().size]);

  useEffect(() => {
    if (!showGeolocationModal) {
      actions.initMappedSheetQuery();

      const initialMapRfiQuery = appState.rfiQuery.set(
        'pages',
        appState.getVisibleSheetEntries().keySeq().toSet()
      );

      actions.setRFIQuery(initialMapRfiQuery);

      if (appState.getVisibleSheetEntries().size === 0) {
        const interaction = new ViewMapLayers();
        mapOverlayMachineService.send({
          type: MapOverlayMachineEventsEnum.OPEN_MAP_LAYERS_OVERLAY,
          data: { interaction },
        });
      }

      return () => {
        mapOverlayMachineService.send(
          MapOverlayMachineEventsEnum.CLOSE_OVERLAY
        );
        actions.deleteInteractions('PlacePunch');
      };
    }
  }, [appState.geoLocation]);

  useEffect(() => {
    actions.getProjectGroups();
    actions.getPunches(appState.selectedProject);
    actions.getRFIs();
  }, []);

  useEffect(() => {
    setShowGeolocationModal(shouldShowGeolocationModal(project?.geolocation));
  }, [project?.geolocation]);

  const filteredMappedSheetVersions = appState
    .getVisibleSheetEntries()
    .map((entry) => entry.mappedSheetVersion)
    .valueSeq();

  const mapLayerOverlayOpen = appState.interactions.some(
    (i) => i.type === 'ViewMapLayers'
  );

  const rightOverlayOpen = appState.interactions.some((i) =>
    ['ViewRFI', 'ViewRFIList', 'UpdatePunch'].includes(i.type)
  );

  const buttonLeftShift = mapLayerOverlayOpen ? 477 : 0;
  const buttonRightShift = rightOverlayOpen ? 477 : 0;

  const postRFIInteraction = appState.interactions.some(
    (i) => i.type === 'PostRFI'
  );

  const placePunchInteraction = appState.interactions.some(
    (i) => i.type === 'PlacePunch'
  );

  let mapHeight = `calc(100vh - 96px - ${bannerHeight}px`;

  if (postRFIInteraction || placePunchInteraction) {
    mapHeight = `calc(100vh - 168px - ${bannerHeight}px)`;
  }

  return (
    <div>
      <div className="row">
        <Link className="bgd-white bb-3" to={'/my-workspace'}>
          {Boolean(environmentVars.BB_BRANDING) === false ? (
            <ProjectRoverHeader width={144} theme="dark" />
          ) : (
            <BluebeamLogo width={124} location="mapPage" />
          )}
        </Link>
        <TopNav
          items={() => {
            return (
              <div className="font-size-7 black-2 medium ml4">
                {project?.name}
              </div>
            );
          }}
        />
      </div>
      <MapPageSubheader />
      {showGeolocationModal && project && (
        <GeolocationModal
          project={project}
          onCloseModal={handleCloseGeoLocationModal}
        />
      )}
      {postRFIInteraction ? <PostRFIMessageOverlay /> : null}
      {placePunchInteraction ? <PlacePunchMessageOverlay /> : null}
      <ECSWorldProvider>
        <MarkupKitProvider>
          <ECSMap mapHeight={mapHeight} buttonLeftShift={buttonLeftShift}>
            {filteredMappedSheetVersions.map((msv) => (
              <ECSMapSheet key={msv.sheetid} mappedSheetVersion={msv} />
            ))}
            <ManagePlacePunchInteraction />
            <ManageUpdatePunchInteraction />

            <ManagePostRFIInteraction />
            <ManageViewRFIInteraction />
          </ECSMap>
        </MarkupKitProvider>
      </ECSWorldProvider>
      {/* Keeping the ReactMapGL version of Map around for a bit as reference */}
      {/* <Map>
        <MappedSheetVersionSources />
      </Map> */}
      {filteredMappedSheetVersions.toList().size >= 1 &&
      !postRFIInteraction &&
      !placePunchInteraction ? (
        <div
          className="pos-a flex-center"
          style={{
            width: `calc(100vw - ${buttonLeftShift + buttonRightShift})`,
            left: buttonLeftShift,
            right: buttonRightShift,
            bottom: '3rem',
            pointerEvents: 'none',
          }}
        >
          <PlacePunchBtn />
          <div className="wrem-1" />
          <PlaceRFIBtn
            sheetid={filteredMappedSheetVersions.getIn([0, 'sheetid'], '')}
            pageid={filteredMappedSheetVersions.getIn([0, 'pageid'], '')}
          />
        </div>
      ) : null}

      {postRFIInteraction ? <PostRFIFooter /> : null}
      {placePunchInteraction ? <PlacePunchFooter /> : null}
    </div>
  );
};

export default withAppStore<Props>(MapPage);
